import { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Promise from 'bluebird';
// Hooks
import { useAuth } from './useAuth';
import { setWalletLoading, setWalletState } from './slices/walletSlice';
// Utils
import { brand } from '../brand/brand';
import { getUserWalletItem } from '../resources/wallet-service.resource';
import { getUserTokenBalance } from '../resources/chain-ledger-service.resource';
import { buildCoinDataObj, extractCoinData, nullCoinDataObj } from '../utils/wallet';
import usdcIcon from '../assets/icons/logos/usdc_icon.png';
import { getGiveUsdPrice } from '~src/resources/give-api.resource';

const initErrorState = {
  coin: false,
  server: false,
};

export const useWallet = () => {
  const { fetcher } = useAuth();
  const dispatch = useDispatch();
  const { walletCoins, ethWallet, walletCoinState } = useSelector(
    (state) => state.reducer.walletReducer
  );
  const [hasError, setHasError] = useState(initErrorState);
  const handleSetHasError = (error) => setHasError((prev) => ({ ...prev, ...error }));

  const getWalletItem = useCallback(async (symbol)  => {
    const coinData = await fetcher(getUserWalletItem({ symbol }));
    if (!coinData.icon) {
      switch (symbol) {
        case 'ETH':
          coinData.icon = "/images/eth-diamond-purple.png"
          break;
        case 'BTC':
          coinData.icon = "/images/bitcoin-logo.png"
          break;
        case 'USDC':
          coinData.icon = usdcIcon.src;
          break;
        case 'GIVE':
          coinData.icon = brand.brandTokenERC.coinLogo;
          const priceRes = await fetcher(getGiveUsdPrice());
          coinData.balanceUsd = (coinData.balance * (priceRes.price?.usd || 0.00));
          break;
        default:
          coinData.icon = "/images/default-blockchain.png"
          break;
      }
    }
    return coinData.balance ? { coinData } : null;
  }, [fetcher]);

  const getWalletItems = useCallback(async (setLoading) => {
    console.log("Getting wallet items....");
    if (setLoading) dispatch(setWalletLoading(true));
    let symbols = [...brand.pages.dashboard.walletCoins];
    const coins = await Promise.map(symbols, async (symbol) => {
      try {
        return await getWalletItem(symbol);
      } catch ({ response: { status } }) {
        const isUsdc = symbol === 'USDC';
        //if (!isUsdc) handleSetHasError({ coin: true });

        if (status === 500) {
          if (symbol === 'ETH') return handleSetHasError({ server: true });
          else return nullCoinDataObj(isUsdc, symbol);
        }

        if (status === 404) {
          // Get coin data from redux store
          const coinState = walletCoins.find((coin) => coin.coinSymbol === symbol);
          return coinState
            ? buildCoinDataObj(coinState)
            : nullCoinDataObj(isUsdc, symbol);
        }
      }
    });

    //if using chain ledger for brand coin, update balance
    if (brand.chainLedgerAPI) {
      //coin address will be the same as ETH address
      const ethAddress = coins.find((coin) => coin.coinData.symbol === 'ETH');

      if (ethAddress.coinData.address) {
        const coin = brand.coin;
        try {
          //returns a hex value that must be parsed, and is wei so need to divide by 100000000
          const coinData = await getUserTokenBalance(
            ethAddress.coinData.address,
            brand.chainLedgerAPI
          );
          //replace brand coin balance with balance value from api call
          const coinIndex = coins.indexOf(
            coins.find((coin) => coin.coinData.symbol === brand.coin)
          );
          //api will return success but no valid result so check that there is a result prior to setting new balance
          coinData.result &&
            (coins[coinIndex].coinData.balance =
              parseInt(coinData.result, 16) / 100000000);
        } catch {
          const coinIndex = coins.findIndex(
            (coin) => coin.coinData.symbol === brand.coin
          );
          coins[coinIndex].coinData.hasError = true;
        }
      }
    }

    if (setLoading) dispatch(setWalletLoading(false));
    return coins.map(extractCoinData);
  }, [getWalletItem, walletCoins]);

  const updateWalletStatus = useCallback(async () => {
    const walletItemsArr = await getWalletItems();
    // only update the wallet if we have items to display
    if (walletItemsArr.length) {
      dispatch(setWalletState(walletItemsArr));
    }
  }, [getWalletItems]);

  //Dashboard Functions
  useEffect(() => {
    console.log("Handle fetch wallet items...");
    async function handleFetchWalletItems() {
      const walletItemsArr = await getWalletItems(true);
      if (walletItemsArr.length) {
        dispatch(setWalletState(walletItemsArr));
      }
    }

    handleFetchWalletItems();

    const updateWalletInterval = setInterval(async () => {
      await updateWalletStatus();
    }, 60 * 1000);

    return function cleanup() {
      if (updateWalletInterval) {
        clearInterval(updateWalletInterval);
      }
    };
  }, []);

  return { walletCoins, ethWallet, walletCoinState, getWalletItems, getWalletItem, updateWalletStatus, hasError };
};
